<template>
  <div class="video-container">
    <div class="header">
      <span class="title">选择策略</span>
      <el-select v-model="strategy" class="strategy-select">
        <el-option
          v-for="item in options"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
    </div>
    <div class="main">
      <div class="main-up">
        <div class="main-up-content">
          <div v-if="!percentage && !videoUrl" class="upload-placeholder">
            <el-icon :size="100" class="upload-icon"><VideoCamera /></el-icon>
            <p class="upload-tip">
              点击下方【本地上传】后，点击【生成】依据策略生成U盘可用文件
            </p>
          </div>

          <div v-if="videoUrl && !percentage" class="video-preview">
            <video
              ref="videoRef"
              controls
              class="preview-video"
              :src="videoUrl"
              @ended="onVideoEnded"
              loop
            ></video>
          </div>

          <div class="progress-container" v-if="percentage">
            <div class="progress-wrapper" v-if="!auditResult && !isGenerating">
              <el-progress
                class="custom-progress"
                :status="status"
                :text-inside="true"
                :stroke-width="20"
                :percentage="percentage"
              >
              </el-progress>
              <div class="status-text">
                {{ uploadMap[status] || "上传中" }}
              </div>
              <div v-if="status === 'exception'" class="error-text">
                上传失败,请重试
              </div>
            </div>

            <div class="watermark-loading" v-if="isGenerating && !auditResult">
              <el-icon class="loading-icon is-loading"><Loading /></el-icon>
              <div class="loading-text">正在进行无感水印,请稍后...</div>
            </div>

            <div class="audit-result" v-if="auditResult">
              <div class="audit-message">
                {{ auditMessage }}
              </div>
              <div v-if="loadable" class="img-download">
                <div class="download-btn" @click="download">
                  <img
                    class="download-icon"
                    :src="downloadImg"
                    alt="download"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="main-down">
        <div class="main-down-action">
          <div class="file-upload-container">
            <el-input
              size="large"
              class="upload-input"
              placeholder="请选择视频文件"
              readonly
              :value="backupFile.name || ''"
            >
              <template #prefix>
                <el-icon class="file-icon"><Document /></el-icon>
              </template>
              <template #append>
                <el-upload
                  :show-file-list="false"
                  action="#"
                  :auto-upload="true"
                  :http-request="customUpload"
                  :before-upload="beforeUpload"
                  :disabled="isGenerating || isUploading"
                  accept=".mov,.mp4"
                >
                  <template #trigger>
                    <el-button
                      class="upload-btn"
                      type="primary"
                      size="large"
                      :disabled="isGenerating || isUploading"
                    >
                      <el-icon class="upload-icon"><Upload /></el-icon>
                      {{ isUploading ? "上传中..." : "本地上传" }}
                    </el-button>
                  </template>
                </el-upload>
              </template>
            </el-input>
          </div>
          <el-button
            class="audit-btn"
            size="large"
            :disabled="status !== 'success' || isGenerating"
            :loading="isGenerating"
            type="primary"
            @click="generate"
          >
            <el-icon class="audit-icon" v-if="!isGenerating"><Check /></el-icon>
            {{ isGenerating ? "处理中..." : "无感水印" }}
          </el-button>
        </div>
        <div class="main-down-text">支持 MP4、MOV 格式</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

import { ElMessage, ElNotification } from "element-plus";
import { Loading } from "@element-plus/icons-vue";
import { generateVideo, downloadVideoFile } from "../../api/index";
import downloadImg from "../../assets/downloadImg.png";
import { AuditResultMap, uploadMap } from "../../enum";
import VideoChunkUploader from "../../utils/videoChunkUploader";

type UploadResultContent = {
  filename?: string;
  path?: string;
  file_path?: string;
  status_code?: string;
};

type UploadResult = {
  code?: string;
  message?: string;
  content?: UploadResultContent;
};

const strategy = ref("1");
const options = [
  {
    value: "1",
    label: "默认策略",
  },
];

const videoRef = ref<HTMLVideoElement | null>(null);
const videoUrl = ref("");

const onVideoEnded = () => {
  if (videoRef.value) {
    videoRef.value.currentTime = 0;
    videoRef.value.play();
  }
};

const beforeUpload = () => true;

const backupFile = ref<File>({} as File);
const generateFilePath = ref("");

const percentage = ref(0);
const status = ref("");

const loadable = computed(() => auditResult.value === "2");
const auditResult = ref("");
const auditMessage = ref("");
const isGenerating = ref(false);
const isUploading = ref(false);

const uploaderRef = ref<VideoChunkUploader | null>(null);

const resetPreview = () => {
  if (videoUrl.value) {
    URL.revokeObjectURL(videoUrl.value);
    videoUrl.value = "";
  }
};

const customUpload = async (file: { file: File }) => {
  uploaderRef.value?.abort();
  backupFile.value = file.file;
  percentage.value = 0;
  status.value = "";
  auditResult.value = "";
  auditMessage.value = "";
  isUploading.value = true;

  videoUrl.value = URL.createObjectURL(file.file);

  const uploader = new VideoChunkUploader({
    file: file.file,
    chunkSize: 2 * 1024 * 1024,
    uploadUrl: "/api/upload/video_chunk",
    onProgress: (progress: number) => {
      percentage.value = progress;
    },
    onSuccess: (result: UploadResult) => {
      const code = result?.code ?? "success";
      const message = result?.message ?? "上传成功";
      const content = (result?.content ?? {}) as UploadResultContent;

      if (code === "success") {
        percentage.value = 100;
        status.value = "success";
        const filename =
          content.filename || content.path || content.file_path || "";
        if (filename) {
          generateFilePath.value = filename;
        }
      } else {
        status.value = "exception";
        resetPreview();
        ElMessage.error(message || "上传失败");
      }

      ElNotification({
        title: message,
        type: code === "success" ? "success" : "error",
      });
    },
    onError: (error: Error) => {
      status.value = "exception";
      percentage.value = 0;
      resetPreview();
      ElMessage.error(error.message || "上传失败");
      ElNotification({
        title: error.message || "上传失败",
        type: "error",
      });
    },
  });

  uploaderRef.value = uploader;
  try {
    await uploader.start();
  } finally {
    uploaderRef.value = null;
    isUploading.value = false;
  }
};

const generate = async () => {
  isGenerating.value = true;
  auditResult.value = "";
  auditMessage.value = "";

  try {
    const response = await generateVideo(generateFilePath.value);
    const { code, message } = response;
    const content = (response.content || {}) as UploadResultContent;
    auditResult.value = content.status_code || "0";
    auditMessage.value = message || AuditResultMap[auditResult.value];

    const prefix = "output-";
    const contentPath = content.path || "";
    if (contentPath.startsWith(prefix)) {
      generateFilePath.value = contentPath.substring(prefix.length);
    } else {
      generateFilePath.value = contentPath;
    }
    ElNotification({
      title: message,
      type: code === "success" ? "success" : "error",
    });
  } catch (error) {
    ElMessage.error("无感水印处理失败，请重试");
    ElNotification({
      title: "无感水印处理失败",
      type: "error",
    });
  } finally {
    isGenerating.value = false;
  }
};
// 下载
const download = async () => {
  try {
    const res = await downloadVideoFile(generateFilePath.value, "download");
    if (res?.code === "success") {
      ElMessage.success(res?.message || "下载成功");
      window.open(res?.content?.download_url, "_blank");
    } else {
      ElMessage.error(res?.message || "下载失败");
    }
  } catch (error) {
    console.error("下载失败", error);
    ElMessage.error("下载失败");
  }
};
</script>
<style lang="scss" scoped>
.video-container {
  padding: 24px;

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 32px;

    .title {
      font-size: 18px;
      font-weight: 600;
      margin-right: 24px;
      color: #333;
    }

    .strategy-select {
      width: 180px;
    }
  }

  .main {
    background: linear-gradient(145deg, #2c3e50, #3498db);
    min-height: 500px;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    position: relative;

    &-up {
      color: #fff;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      position: relative;
      overflow: hidden;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(10px);
      }

      &-content {
        position: relative;
        text-align: center;
        width: 100%;
        max-width: 600px;

        .video-preview {
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

          .preview-video {
            width: 100%;
            height: auto;
            max-height: 400px;
            object-fit: contain;
            background: #000;
          }
        }

        .upload-placeholder {
          padding: 40px;
          border: 2px dashed rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          transition: all 0.3s ease;

          &:hover {
            border-color: rgba(255, 255, 255, 0.5);
          }

          .upload-icon {
            color: #fff;
            margin-bottom: 24px;
            opacity: 0.8;
            filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
          }

          .upload-tip {
            font-size: 16px;
            color: rgba(255, 255, 255, 0.9);
            line-height: 1.6;
          }
        }

        .progress-container {
          background: rgba(255, 255, 255, 0.1);
          padding: 24px;
          border-radius: 12px;
          backdrop-filter: blur(10px);

          .custom-progress {
            margin-bottom: 16px;
          }

          .status-text {
            font-size: 16px;
            color: rgba(255, 255, 255, 0.9);
          }

          .error-text {
            color: #ff6b6b;
            margin-top: 8px;
            font-size: 14px;
          }

          .watermark-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;

            .loading-icon {
              font-size: 48px;
              color: rgba(255, 255, 255, 0.9);
              margin-bottom: 16px;
              animation: rotating 2s linear infinite;
            }

            .loading-text {
              font-size: 18px;
              color: rgba(255, 255, 255, 0.9);
              font-weight: 500;
            }
          }
        }

        .audit-result {
          .audit-message {
            font-size: 18px;
            margin-bottom: 24px;
            color: rgba(255, 255, 255, 0.9);
          }
        }
      }
    }

    &-down {
      padding: 24px;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(20px);

      &-action {
        display: flex;
        align-items: center;
        gap: 16px;

        .file-upload-container {
          flex: 1;
          position: relative;
        }
      }

      &-text {
        color: rgba(255, 255, 255, 0.7);
        margin-top: 12px;
        font-size: 14px;
      }
    }
  }

  .download-btn {
    cursor: pointer;
  }
}

:deep(.el-input__wrapper) {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  backdrop-filter: blur(10px);
  box-shadow: none;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.15);
  }

  .el-input__inner {
    color: #fff;
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }
}

:deep(.el-input-group__append),
:deep(.el-input-group__prepend) {
  background: transparent;
  border: none;
  padding: 0 4px;
}

:deep(.el-progress-bar__outer) {
  background: rgba(255, 255, 255, 0.1);
}

:deep(.el-progress-bar__inner) {
  background: linear-gradient(90deg, #1547ec, #4b7bff);
}

:deep(.el-progress) {
  .el-progress__text {
    color: #fff;
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
